import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, Container, Row, Tab, Tabs } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { Parallax } from 'react-scroll-parallax';
import { m } from 'framer-motion';
import IconWithText from '../../Components/IconWithText/IconWithText';
import Accordions from '../../Components/Accordion/Accordion';
import PricingTable01 from '../../Components/PricingTable/PricingTable01';
import SideButtons from '../../Components/SideButtons';
import { fadeIn } from '../../Functions/GlobalAnimations';
import { transformAbonnementData } from '../../Components/PricingTable/PricingTableData';
import config from '../../config';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import { Loader2 } from 'lucide-react';
import { ChevronDown, Shield, Scale, Users, Mail, Phone } from "lucide-react"


const PricingPackagesPage = ({ style }) => {
  const { t, i18n } = useTranslation(); // الوصول إلى الترجمات وإعدادات i18n
  const isRtl = i18n.language === 'ar'; // تحديد اتجاه النص

  // Déterminer la direction en fonction de la langue
  const direction = i18n.language === 'ar' ? 'rtl' : 'ltr';
  
  const [monthlyData, setMonthlyData] = useState([]);
  const [yearlyData, setYearlyData] = useState([]);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);


  const [billingCycle, setBillingCycle] = useState("monthly"); // "monthly" | "annual"
  const displayedPlans = React.useMemo(() => {
  const source = billingCycle === "monthly" ? monthlyData : yearlyData;
  return source.map(plan => ({
    ...plan,
    // Tu peux ajouter/modifier des champs si besoin
    period: billingCycle === "annual" ? "سنوي" : "شهري",
    discount: billingCycle === "annual" ? 20 : 0, // ← à adapter selon ta vraie logique
  }));
}, [billingCycle, monthlyData, yearlyData]);

const [activeAccordion, setActiveAccordion] = useState(null);

const toggleAccordion = (index) => {
  setActiveAccordion(activeAccordion === index ? null : index);
};
  // بيانات IconWithText مترجمة
  const IconWithTextData = [
    {
      icon: Shield,
      title: t('pricing_packages.icon_with_text.secure_payments.title'),
      content: t('pricing_packages.icon_with_text.secure_payments.content'),
    },
    {
      icon: Scale,
      title: t('pricing_packages.icon_with_text.tailored_advice.title'),
      content: t('pricing_packages.icon_with_text.tailored_advice.content'),
    },
    {
      icon: Users,
      title: t('pricing_packages.icon_with_text.customer_satisfaction.title'),
      content: t('pricing_packages.icon_with_text.customer_satisfaction.content'),
    },
  ];

  // بيانات الأكورديون مترجمة
  const AccordionData = [
    {
      title: t('pricing_packages.accordion.royalty_free.title'),
      content: t('pricing_packages.accordion.royalty_free.content'),
    },
    {
      title: t('pricing_packages.accordion.item_end_product.title'),
      content: t('pricing_packages.accordion.item_end_product.content'),
    },
    {
      title: t('pricing_packages.accordion.permitted_end_products.title'),
      content: t('pricing_packages.accordion.permitted_end_products.content'),
    },
    {
      title: t('pricing_packages.accordion.modify_item.title'),
      content: t('pricing_packages.accordion.modify_item.content'),
    },
    {
      title: t('pricing_packages.accordion.use_covered.title'),
      content: t('pricing_packages.accordion.use_covered.content'),
    },
  ];
const faqItems = [
    {
      title: t('pricing_packages.accordion.royalty_free.title'),
      content: t('pricing_packages.accordion.royalty_free.content'),
    },
    {
      title: t('pricing_packages.accordion.item_end_product.title'),
      content: t('pricing_packages.accordion.item_end_product.content'),
    },
    {
      title: t('pricing_packages.accordion.permitted_end_products.title'),
      content: t('pricing_packages.accordion.permitted_end_products.content'),
    },
    {
      title: t('pricing_packages.accordion.modify_item.title'),
      content: t('pricing_packages.accordion.modify_item.content'),
    },
    {
      title: t('pricing_packages.accordion.use_covered.title'),
      content: t('pricing_packages.accordion.use_covered.content'),
    },
  ];
  // جلب بيانات الاشتراكات
  const fetchSubscriptions = useCallback(async () => {
    try {
      const token = localStorage.getItem('accessToken');
      setIsAuthenticated(!!token);
      setLoading(true);

      // جلب الاشتراكات الشهرية
      const monthlyResponse = await axios.get(`${config.SITE_API_URL}/allsubscriptionmensuel`, {
        headers: { Authorization: `Bearer ${token}`, 'x-api-key': config.SITE_API_KEY },
        params: { lang: i18n.language },
        timeout: 10000,
      });
      setMonthlyData(transformAbonnementData(monthlyResponse.data, i18n.language));

      // جلب الاشتراكات السنوية
      const yearlyResponse = await axios.get(`${config.SITE_API_URL}/allsubscriptionannuel`, {
        headers: { Authorization: `Bearer ${token}`, 'x-api-key': config.SITE_API_KEY },
        params: { lang: i18n.language },
        timeout: 10000,
      });
      setYearlyData(transformAbonnementData(yearlyResponse.data, i18n.language));
    } catch (error) {
      console.error('خطأ في جلب الاشتراكات:', error);
      toast.error(error.response?.data?.message || t('pricing_packages.error_fetching_subscriptions'));
    } finally {
      setLoading(false);
    }
  }, [i18n.language, t]);

  useEffect(() => {
    fetchSubscriptions();
  }, [fetchSubscriptions]);
  
const [phoneNumber, setPhoneNumber] = useState(null); // État pour stocker le numéro de téléphone

  useEffect(() => {
    const fetchAdminPhone = async () => {
      try {
        const response = await axios.get(`${config.SITE_API_URL}/admin-phone`,{
            headers: { 
            'x-api-key': config.SITE_API_KEY
           }});
        if (response.data.success) {
          // Ajouter l'indicatif de pays si nécessaire (par exemple, +216 pour la France)
          const formattedPhone = `+216${response.data.phone}`; // Remplacez +216 par l'indicatif approprié
          setPhoneNumber(formattedPhone);
        } else {
          toast.error('Erreur lors de la récupération du numéro de téléphone de l\'admin.');
        }
      } catch (error) {
        toast.error('Erreur lors de la récupération du numéro de téléphone de l\'admin.');
      }
    };

    fetchAdminPhone();
  }, []);

  const whatsappLink = phoneNumber ? `https://wa.me/${phoneNumber.replace(/[^0-9]/g, '')}?text=Bonjour,%20je%20souhaite%20une%20assisstance` : '#';

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen" style={{ direction: direction }}>
        <Loader2 className={`animate-spin ${isRtl ? 'ml-2 mr-0' : 'mr-2 ml-0'}`} size={24} />
        <p>{t('pricing_packages.loading')}</p>
      </div>
    );
  }

  return (
    <div style={{ ...style, direction: direction}}>
      <SideButtons />
      {/* قسم التمرير المنظري (Parallax Scrolling) */}
      <m.div className="h-[380px] overflow-hidden sm:h-[365px] relative" {...fadeIn}>
              <Parallax
                className="lg-no-parallax bg-cover absolute top-0 left-0 w-full h-full"
                translateY={[-40, 40]}
                style={{ backgroundImage: `url(/assets/img/webp/portfolio-bg.webp)` }}
              />
              <Container className="h-full relative">
                <Row className="items-stretch justify-center h-[450px] md:h-[375px] sm:h-[350px]">
                  <Col xl={6} lg={7} md={8} xs={12} className="text-center flex justify-center flex-col">
                    <h2 className="text-blue-900 mb-[0px] font-serif font-medium -tracking-[1px] my-auto xs:mb-[0px]">
                {t('pricing_packages.page_title')}
              </h2>
            </Col>
          </Row>
        </Container>
      </m.div>
      {/* نهاية قسم التمرير المنظري */}


      {/* قسم المزايا */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid min-md:grid-cols-3 gap-8">
            {IconWithTextData.map((feature, index) => {
              const Icon = feature.icon
              return (
                <div
                  key={index}
                  className="bg-white p-8 rounded-lg shadow-sm hover:shadow-md transition-shadow animate-fade-in-up"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="mb-4">
                    <Icon size={48} style={{ color: "#f29f2f" }} className="mx-auto" />
                  </div>
                  <h3 className="text-xxl font-bold mb-3 text-center" style={{ color: "#003479" }}>
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 text-center text-sm">{feature.content}</p>
                </div>
              )
            })}
          </div>
        </div>
      </section>
      {/* نهاية قسم المزايا */}

{/* قسم الاشتراكات */}
<m.section
  className="py-20 bg-white"
  {...fadeIn}
>
  {/* Billing Cycle Toggle */}
  <div className="max-w-7xl mx-auto px-4 flex justify-center items-center mb-12">
    <div className="flex items-center gap-4 min-sm:gap-6 bg-gray-100 p-1.5 rounded-full shadow-sm">
      <button
        onClick={() => setBillingCycle("monthly")}
        className={`px-5 min-sm:px-7 py-2.5 rounded-full font-medium text-sm min-sm:text-base transition-all duration-300 ${
          billingCycle === "monthly"
            ? "text-white shadow-md scale-105"
            : "text-gray-700 hover:text-gray-900"
        }`}
        style={{
          backgroundColor: billingCycle === "monthly" ? "#003479" : "transparent",
        }}
      >
        {t('pricing_packages.tabs.monthly')}
      </button>

      <button
        onClick={() => setBillingCycle("annual")}
        className={`px-5 min-sm:px-7 py-2.5 rounded-full font-medium text-sm min-sm:text-base transition-all duration-300 ${
          billingCycle === "annual"
            ? "text-white shadow-md scale-105"
            : "text-gray-700 hover:text-gray-900"
        }`}
        style={{
          backgroundColor: billingCycle === "annual" ? "#003479" : "transparent",
        }}
      >
        {t('pricing_packages.tabs.yearly')}
      </button>
          {billingCycle === "annual" && (
            <div className="ar-text-left mr-4 text-sm font-semibold p-2 rounded" style={{ color: "#f29f2f" }}>
              توفير يصل إلى 20%
            </div>
          )}
    </div>
  </div>

  {/* Titre + sous-titre */}
  <div className="max-w-7xl mx-auto px-4 text-center mb-12">
    <h2 className="text-3xl min-sm:text-4xl font-bold mb-3" style={{ color: "#003479" }}>
      {t('pricing_packages.choose_plan')} {/* ou ton texte personnalisé */}
    </h2>
    <p className="text-gray-600 text-base min-sm:text-lg">
      {i18n.language === 'ar'
        ? 'ابدأ من الباقات الصغيرة والمتوسطة إلى المتقدمة'
        : 'Des formules adaptées aux petites, moyennes et grandes structures'}
    </p>
  </div>

  {/* Grille des cartes */}
  <div className="max-w-7xl mx-auto px-4">
    <div className="grid grid-cols-1 min-sm:grid-cols-2 min-lg:grid-cols-3 gap-6 min-md:gap-8">
      {displayedPlans.map((plan, index) => {
        // Déterminer le titre et le lien du bouton en fonction de l'état de connexion et de la langue
                    const buttonTitle = isAuthenticated 
                        ? (i18n.language === 'fr' ? "Acheter abonnement" : "شراء الاشتراك") 
                        : (i18n.language === 'fr' ? "Inscrivez-vous" : "قم بالتسجيل");
                    const buttonLink = plan._id
                                        ? (isAuthenticated ? `/checkout_subscription/${plan._id}` : "/login")
                                        : "/login"; // Fallback to /login if _id is missing
                    const currency = i18n.language === 'fr' ? 'DT' : 'دينار';
        return (
          <div
          key={plan._id || index}
          className={`relative rounded-xl overflow-hidden transition-all duration-300 hover:-translate-y-2 ${
            plan.popular?.isPopular
              ? "ring-2 ring-[#f29f2f] shadow-2xl scale-[1.03] md:scale-105"
              : "shadow-lg hover:shadow-xl"
          }`}
          style={{
            animationDelay: `${index * 0.08}s`,
            backgroundColor: "#003479",
            border: "2px solid #f29f2f",
          }}
        >

          <div>
            <div
              className="text-center py-2.5 text-white font-semibold text-xlg"
              style={{ backgroundColor: "#f29f2f" }}
            >
              {plan.title}
            </div>
<div className="p-6 min-sm:p-8">

            <div className="my-7">
              <div
                className={`text-xxl min-sm:text-xxl font-extrabold`}
                style={{ color: "#f29f2f"}}
              >
                {plan.price} <span className="text-xl">{currency}</span>
              </div>
            <p
              className={`text-sm mb-5 text-gray-200`}
            >
              {plan.term}
            </p>
            </div>

            <ul className="space-y-3 mb-8">
              {plan.plans.map((feature, i) => (
                <li
                  key={i}
                  className={`text-xlg flex items-center gap-2.5 text-[#f29f2f]`}
                >
                  <span
                    className="flex-shrink-0 text-xl"
                    style={{ color: "#58bceb" }}
                  >
                    ✓
                  </span>
                  <span dangerouslySetInnerHTML={{ __html: feature }} />
                </li>
              ))}
            </ul>
<a
                href={buttonLink}
                className={`block w-full py-3.5 text-center rounded-lg font-semibold text-base transition-all duration-300 hover:scale-[1.03] bg-white text-[#003479] hover:bg-gray-100`}
              >
                {buttonTitle}
              </a>
          </div>
</div>
        </div>
        )
      })}
    </div>
  </div>
</m.section>
      {/* نهاية قسم الاشتراكات */}
{/* قسم الأسئلة الشائعة */}
<m.section className="py-20 bg-gray-50" {...fadeIn}>
  <div className="max-w-7xl mx-auto px-4">
    {/* Titre FAQ */}
    <div className="text-center mb-12">
      <h2 className="text-3xl min-sm:text-xxlg font-bold mb-4" style={{ color: "#003479" }}>
        {t('pricing_packages.faq.title') || "الأسئلة الشائعة"}
      </h2>
      <p className="text-gray-600 text-lg">
        {i18n.language === 'ar' ? (
          <>
            هل لديك أسئلة؟{" "}
            <Link
              to="/contact"
              className="font-semibold hover:underline"
              style={{ color: "#0879bd" }}
            >
              تواصل معنا
            </Link>
          </>
        ) : (
          <>
            Vous avez des questions ?{" "}
            <Link
              to="/contact"
              className="font-semibold hover:underline"
              style={{ color: "#0879bd" }}
            >
              Contactez-nous
            </Link>
          </>
        )}
      </p>
    </div>

    {/* Accordéon FAQ */}
    <div className="space-y-4">
      {faqItems.map((item, index) => (
        <div
          key={index}
          className="bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-all duration-300"
          style={{ animationDelay: `${index * 0.06}s` }}
        >
          <button
            onClick={() => toggleAccordion(index)}
            className={`w-full px-5 min-sm:px-6 py-4 flex items-center justify-between ${isRtl ? 'text-left' : 'text-right'} hover:bg-gray-50 transition-colors`}
          >
            <span className="font-semibold text-gray-900 text-base min-sm:text-lg">
              {item.title}
            </span>
            <ChevronDown
              size={22}
              style={{ color: "#0879bd" }}
              className={`transition-transform duration-300 ${
                activeAccordion === index ? "rotate-180" : ""
              }`}
            />
          </button>

          {activeAccordion === index && (
            <div className="px-5 sm:px-6 py-5 bg-gray-50 border-t border-gray-200">
              <p className="text-gray-700 leading-relaxed text-base">
                {item.content}
              </p>
            </div>
          )}
        </div>
      ))}
    </div>
  </div>
</m.section>

{/* Section Contact direct */}
<m.section className="py-20 bg-white" {...fadeIn}>
  <div className="max-w-7xl mx-auto px-4">
    <div className="text-center mb-12">
      <h2 className="text-3xl min-sm:text-xxlg font-bold mb-4" style={{ color: "#003479" }}>
        {i18n.language === 'ar' ? "تواصل معنا مباشرة" : "Contactez-nous directement"}
      </h2>
      <p className="text-gray-600 text-lg">
        {i18n.language === 'ar'
          ? "فريقنا جاهز للإجابة على جميع استفساراتك"
          : "Notre équipe est prête à répondre à toutes vos questions"}
      </p>
    </div>

    <div className="grid min-md:grid-cols-2 gap-6 lg:gap-8">
      {/* Carte Email */}
      <div
        className="p-8 rounded-xl text-white shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-1"
        style={{ background: "linear-gradient(135deg, #0879bd 0%, #005fa3 100%)" }}
      >
        <Mail size={40} className="mb-5 mx-auto block" />
        <h3 className="text-xlg font-bold mb-3 text-center">
          {i18n.language === 'ar' ? "البريد الإلكتروني" : "Email"}
        </h3>
        <p className="mb-4 text-center opacity-90">
          {i18n.language === 'ar'
            ? "تواصل معنا عبر البريد الإلكتروني"
            : "Contactez-nous par e-mail"}
        </p>
        <a
          href="mailto:contact@jaafaracademy.com"
          className="block text-center font-semibold hover:underline text-lg"
        >
          contact@jaafaracademy.com
        </a>
      </div>

      {/* Carte Téléphone / WhatsApp */}
      <div
        className="p-8 rounded-xl text-white shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-1"
        style={{ background: "linear-gradient(135deg, #003479 0%, #002050 100%)" }}
      >
        <Phone size={40} className="mb-5 mx-auto block" />
        <h3 className="text-xlg font-bold mb-3 text-center">
          {i18n.language === 'ar' ? "الهاتف / واتساب" : "Téléphone / WhatsApp"}
        </h3>
        <p className="mb-4 text-center opacity-90">
          {i18n.language === 'ar'
            ? "اتصل بنا أو تواصل عبر واتساب"
            : "Appelez-nous ou contactez-nous sur WhatsApp"}
        </p>
        <a
          href={whatsappLink}
          className="block text-center font-semibold hover:underline text-lg"
    dir="ltr"
        >
          {phoneNumber ? phoneNumber.replace(/^\+216/, '').replace(/\B(?=(\d{2})+(?!\d))/g, " ") : "—"}
        </a>
      </div>
    </div>
  </div>
</m.section>
      {/* نهاية قسم الأسئلة الشائعة */}
    </div>
  );
};

export default PricingPackagesPage;